import React, { useState, useEffect, forwardRef } from 'react';
import { Input } from './input';
import { Label } from './label';
import { cn } from '../../lib/utils';
import { formatIndianCurrency, parseIndianNumber, formatAsUserTypes, isValidNumber } from '../../lib/formatters';

export interface CurrencyInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value'> {
  value?: number;
  onChange?: (value: number) => void;
  label?: string;
  error?: string;
  showSymbol?: boolean;
  allowDecimals?: boolean;
  maxValue?: number;
  minValue?: number;
}

const CurrencyInput = forwardRef<HTMLInputElement, CurrencyInputProps>(
  ({ 
    value = 0, 
    onChange, 
    label, 
    error, 
    showSymbol = true, 
    allowDecimals = true,
    maxValue,
    minValue = 0,
    className,
    placeholder = "0.00",
    disabled,
    ...props 
  }, ref) => {
    const [displayValue, setDisplayValue] = useState('');
    const [isFocused, setIsFocused] = useState(false);

    // Update display value when prop value changes
    useEffect(() => {
      if (value || value === 0) {
        if (isFocused) {
          // Show raw number when focused for easier editing
          setDisplayValue(value.toString());
        } else {
          // Show formatted number when not focused
          setDisplayValue(formatIndianCurrency(value, false));
        }
      } else {
        setDisplayValue('');
      }
    }, [value, isFocused]);

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(true);
      // Convert to raw number for editing
      if (value || value === 0) {
        setDisplayValue(value.toString());
      }
      props.onFocus?.(e);
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(false);
      
      // Validate and format on blur
      const numericValue = parseIndianNumber(displayValue);
      
      // Apply min/max constraints
      let constrainedValue = numericValue;
      if (minValue !== undefined && constrainedValue < minValue) {
        constrainedValue = minValue;
      }
      if (maxValue !== undefined && constrainedValue > maxValue) {
        constrainedValue = maxValue;
      }
      
      // Update parent component
      onChange?.(constrainedValue);
      
      // Format display value
      setDisplayValue(formatIndianCurrency(constrainedValue, false));
      
      props.onBlur?.(e);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = e.target.value;
      
      if (isFocused) {
        // When focused, allow raw input but validate
        if (inputValue === '' || isValidNumber(inputValue)) {
          setDisplayValue(inputValue);
        }
      } else {
        // When not focused, format as user types
        const formatted = formatAsUserTypes(inputValue);
        setDisplayValue(formatted);
        
        // Update parent with numeric value
        const numericValue = parseIndianNumber(formatted);
        onChange?.(numericValue);
      }
    };

    return (
      <div className="space-y-2">
        {label && (
          <Label htmlFor={props.id} className={error ? 'text-destructive' : ''}>
            {label}
            {props.required && <span className="text-destructive ml-1">*</span>}
          </Label>
        )}
        
        <div className="relative">
          {showSymbol && (
            <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground pointer-events-none">
              ₹
            </div>
          )}
          
          <Input
            {...props}
            ref={ref}
            type="text"
            value={displayValue}
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            placeholder={showSymbol ? `₹${placeholder}` : placeholder}
            disabled={disabled}
            className={cn(
              showSymbol && 'pl-8',
              error && 'border-destructive focus-visible:ring-destructive',
              className
            )}
          />
        </div>
        
        {error && (
          <p className="text-sm text-destructive">{error}</p>
        )}
        
        {!error && (maxValue || minValue !== undefined) && (
          <p className="text-xs text-muted-foreground">
            {minValue !== undefined && maxValue ? 
              `Range: ₹${formatIndianCurrency(minValue, false)} - ₹${formatIndianCurrency(maxValue, false)}` :
              minValue !== undefined ? 
                `Minimum: ₹${formatIndianCurrency(minValue, false)}` :
                `Maximum: ₹${formatIndianCurrency(maxValue!, false)}`
            }
          </p>
        )}
      </div>
    );
  }
);

CurrencyInput.displayName = 'CurrencyInput';

export { CurrencyInput };